import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AuthService } from '../../../../services.module/auth.service';

import { Router } from '@angular/router';
import { ObligoRequest, VertragHandle } from '../../../../../generated/api/kreditportalBackendAPI.schemas';
import { VertraegeService } from '../../../../providers.module/vertraege.service';
import { ObligoApiService } from '../../../../../generated/api/obligo/obligo.service';
import { ObligoAbrufModel } from './obligo-abruf.model';
import { DateUtilsService } from '../../../../common.component.module/date-utils.service';
import { format } from 'date-fns';

@Component({
    selector: 'obligo',
    templateUrl: './obligo.component.html',
    styleUrls: ['./obligo.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ObligoComponent {

    vertraege = signal<VertragHandle[]>([]);

    model = new ObligoAbrufModel();

    dateSelectionVisible = signal(false);

    vertragsListeDisabled = signal(false);

    downloadButtonDisabled = signal(false);

    dateUtilsService = inject(DateUtilsService);

    constructor(
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly vertraegeService: VertraegeService,
        private readonly obligoApiService: ObligoApiService
    ) {
        this.vertraegeService.getVertraegeNummernListe().subscribe(responseVertraege => {
            this.vertraege.set(responseVertraege);

            if (responseVertraege.length === 1) {
                this.model.vsnrSelection.value = responseVertraege[0].vsnrNumerisch;
            }

            this.vertragsListeDisabled.set(responseVertraege.length <= 1);
        });
    }

    notifyRisk(): void {
        if (this.model.onlyInRisk.value === 'im_risiko') {
            this.model.datumVon.value = '';
            this.model.datumBis.value = '';
            this.dateSelectionVisible.set(false);
        } else {
            this.dateSelectionVisible.set(true);
        }
    }

    onInputFieldKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.downloadObligoliste();
        }
    }

    downloadObligoliste() {
        if (this.model.validateAll()?.length) {
            return;
        }

        this.downloadButtonDisabled.set(true);

        const obligoAnfordernRequest: ObligoRequest = {};

        switch (this.model.vsnrSelection.value) {
            case '':
                this.model.vsnrSelection.valid = false;
                this.model.vsnrSelection.errorMsg = 'Bitte wählen Sie eine Versicherungschein-Nr.';
                break;
            case 'all':
                obligoAnfordernRequest.vsnrs = this.vertraege().map(v => v.vsnrNumerisch);
                obligoAnfordernRequest.zipped = true;
                break;
            default:
                obligoAnfordernRequest.vsnrs = [this.model.vsnrSelection.value];
        }

        obligoAnfordernRequest.requiredFormat = this.model.requiredFormat.value;

        obligoAnfordernRequest.benutzerId = this.authService.determineKundennummer(this.authService.getActiveKunde().vsnr);

        obligoAnfordernRequest.onlyInRisk = this.model.onlyInRisk.value === 'im_risiko';

        if (!obligoAnfordernRequest.onlyInRisk) {
            obligoAnfordernRequest.datumVon = this.model.datumVon.value;
            obligoAnfordernRequest.datumBis = this.model.datumBis.value;
        }

        this.obligoApiService.postObligoAsCsvOrPdf(obligoAnfordernRequest)
            .subscribe({
                next: obligoResponse => {
                    obligoResponse.lists.forEach(obligoResponseEntry => {
                        ObligoComponent.storeFile(
                            obligoResponseEntry.fileName,
                            obligoResponseEntry.content,
                            obligoAnfordernRequest.requiredFormat
                        );
                    });

                    this.downloadButtonDisabled.set(false);
                },
                error: (error) => {
                    console.log(error);
                    this.downloadButtonDisabled.set(false);
                }
            });
    }

    protected datumVorbelegungDiesesJahr() {
        const now = new Date();
        const nowAnfangDesJahres = new Date(now.getFullYear(), 0, 1);

        const datumVon = format(nowAnfangDesJahres, 'dd.MM.yyyy');
        const datumBis = format(now, 'dd.MM.yyyy');

        this.model.datumVon.value = datumVon;
        this.model.datumBis.value = datumBis;
    }

    protected datumVorbelegungLetztesJahr() {
        const now = new Date();
        const anfangLetztenJahres = new Date(now.getFullYear() - 1, 0, 1);
        const endeLetztenJahres = new Date(now.getFullYear() - 1, 11, 31);

        const datumVon = format(anfangLetztenJahres, 'dd.MM.yyyy');
        const datumBis = format(endeLetztenJahres, 'dd.MM.yyyy');

        this.model.datumVon.value = datumVon;
        this.model.datumBis.value = datumBis;
    }

    private static readonly b64toBlob = (b64Data, contentType) => {
        contentType = contentType || '';
        const sliceSize = 512;
        b64Data = b64Data.replace(/^[^,]+,/, '');
        b64Data = b64Data.replace(/\s/g, '');
        const byteCharacters = window.atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: contentType });
    };

    private static storeFile(fileName: string, content: string, format: string): void {
        // Gezippte Inhalte (auch CSV-Zips) werden vom Backend Base64-kodiert geliefert und müssen dekodiert werden.
        // Nur die unkomprimierte CSV kommt als Klartext.
        const isZip = fileName.toLowerCase().endsWith('.zip');
        const blob = ('csv' === format && !isZip)
            ? new Blob([content])
            : ObligoComponent.b64toBlob(content, isZip ? 'application/zip' : format);

        const url = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.setAttribute('href', url);
        anchor.setAttribute('download', fileName);

        anchor.click();
    }

    goBack() {
        this.router.navigate(['/kreditportal/buergschaften']);
    }

}
